import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { Stars03, Lock01 } from '@kula-ai/icons-core';

interface NoteTakerConfigProps {
  config?: Record<string, unknown>;
  fieldProps?: Record<string, unknown>;
  /**
   * Authoritative per-stage mandate sourced FRESH from the interview
   * config/plan (`GET /ats_interviews/new|:id` → `note_taker_mandatory`) —
   * scheduler-accessible (no admin permission) and correctly scoped per
   * job + stage. When provided it REPLACES the shared, staleness-prone
   * `atsJobCreate.currentJob` slice: the scheduling wizard never refetches
   * that slice, so it can serve a stale `mandatory: true` left from when
   * the account mandate master was on. Omitted by adhoc (no plan) and the
   * settings interview-plan flow, which fall back to `currentJob` /
   * `mandatoryOverride`.
   */
  mandatory?: boolean;
  /**
   * Force mandatory on when the org setting can't be read from
   * `atsJobCreate.currentJob` — e.g. the settings interview-plan flow,
   * where mandatory comes from the template's `note_taker_mandatory`.
   */
  mandatoryOverride?: boolean;
}

const FIELD_NAME = 'enable_note_taker';
const NOTE_TAKER_LABEL = 'Enable AI notetaker?';
const NOTE_TAKER_DESCRIPTION =
  'The AI Notetaker automatically captures key points and summaries during meetings. It organizes and highlights information for easy review and follow-up.';
const NOTE_TAKER_DISABLED_TOOLTIP =
  'AI note taker is required for this role as configured by the admin.';

export const useNoteTakerConfig = (props: NoteTakerConfigProps = {}) => {
  const { currentJob = {} } = useSelector(getAtsJobCreate, shallowEqual);

  // Prefer the explicit fresh mandate when the caller supplies it; only
  // fall back to the shared `currentJob` slice (prone to staleness) when no
  // fresh value is available (adhoc / settings-plan flow).
  const resolvedJobMandatory =
    props.mandatory !== undefined
      ? props.mandatory
      : (currentJob?.data?.note_taker?.mandatory ?? false);

  const isNoteTakerMandatory =
    resolvedJobMandatory || Boolean(props.mandatoryOverride);

  const field = useMemo(
    () => ({
      type: 'switch' as const,
      name: FIELD_NAME,
      required: isNoteTakerMandatory,
      condition: {
        operator: 'in',
        field: 'location',
        value: ['zoom', 'google_meet', 'teams'],
      },
      classNames: {
        // Figma: 12px (p-3), no border. Background = secondary-25 (#F8F9FC).
        container: 'bg-secondary-25 p-3 rounded-md',
      },
      disabled: isNoteTakerMandatory,
      ...(props.config || {}),
      fieldProps: {
        switchLabel: NOTE_TAKER_LABEL,
        description: NOTE_TAKER_DESCRIPTION,
        labelLeftElement: <Stars03 size={18} className="!text-primary-500" />,
        // Figma label = primary-500 / 14px / weight 550.
        labelClassName: '!text-primary-500 !font-[550]',
        // Figma description = 14px / 150% / weight 450 / secondary-700.
        descriptionClassName:
          '!text-sm !leading-[150%] !font-[450] !text-secondary-700',
        labelAlign: 'center',
        fullWidth: true,
        labelPosition: 'left',
        // Locked style for the mandatory toggle. Stays `disabled` (field
        // level) so it's non-interactive — click does nothing,
        // cursor-not-allowed, focus skipped. But instead of the Switch's
        // default `disabled:opacity-40` dim we render a lock icon at full
        // opacity (design-system locked look): `checkedContent` puts the
        // lock in the track, `className: '!opacity-100'` cancels the dim,
        // and `disabledTooltip` explains why on hover.
        // NOTE: the kula `Switch` has no first-class `locked` prop yet —
        // this composes it from existing props. Replace with the native
        // variant once components ships one.
        ...(isNoteTakerMandatory
          ? {
              disabledTooltip: NOTE_TAKER_DISABLED_TOOLTIP,
              dimLabelWhenDisabled: false,
              checkedContent: <Lock01 size={14} className="!text-white" />,
              className: '!opacity-100',
            }
          : {}),
        ...(props.fieldProps || {}),
      },
    }),
    [props, isNoteTakerMandatory]
  );

  return { field, isMandatory: isNoteTakerMandatory };
};
